import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { Layout, TableSkeleton, PageHeader, SectionCard, EmptyState } from '../common';
import { Table, Button } from 'antd';
import { DollarSign, Download, Eraser, ChartColumn, ShieldAlert, TriangleAlert, Rows3 } from 'lucide-react';
import dayjs from 'dayjs';
import { fmtCapture } from '../../utils/timeUtils';
import LevantamientoGeneralController from './levantamientoGeneral.controller';
import { DEFAULT_FILTERS, formatPriceName, fmtCount } from './levantamientoShared';
import { LevantamientoFilters, ExportProgressModal, SourceTag, ProductThumb, CapturePrice, RoleTag } from './elements';

class LevantamientoGeneral extends Inherit(LevantamientoGeneralController, Component) {
    state = {
        loading: true,
        dataLoading: false,
        exporting: false,
        exportStep: null,
        exportElapsed: 0,
        exportRowCount: 0,
        accessDenied: false,
        error: false,
        rows: [],
        clientOptions: [],
        storeOptions: [],
        userOptions: [],
        categoryOptions: [],
        segmentOptions: [],
        tagOptions: [],
        gramsOptions: [],
        storeTypeOptions: [],
        dateRange: [dayjs().subtract(30, 'day'), dayjs()],
        filters: { ...DEFAULT_FILTERS },
        pagination: { page: 1, limit: 50, total: 0 }
    };

    get columns() {
        return [
            {
                title: 'Imagen',
                dataIndex: 'image',
                key: 'image',
                width: 64,
                align: 'center',
                render: (src) => <ProductThumb src={src} />
            },
            {
                title: 'Producto',
                dataIndex: 'productName',
                key: 'productName',
                render: (name, r) => <span className="font-medium text-surface-900">{formatPriceName(r)}</span>
            },
            {
                title: 'Código',
                dataIndex: 'barcode',
                key: 'barcode',
                render: (code) => (code ? <span className="font-mono text-xs text-surface-600">{code}</span> : <span className="text-surface-400">—</span>)
            },
            {
                title: 'Fuente',
                dataIndex: 'source',
                key: 'source',
                render: (s) => <SourceTag source={s} />
            },
            { title: 'Piso de venta', dataIndex: 'storeName', key: 'storeName' },
            {
                title: 'Tipo de tienda',
                dataIndex: 'storeType',
                key: 'storeType',
                render: (t) => t || <span className="text-surface-400">—</span>
            },
            { title: 'Cliente', dataIndex: 'clientName', key: 'clientName' },
            {
                title: 'Usuario',
                dataIndex: 'userName',
                key: 'userName',
                render: (u) => u || <span className="text-surface-400">—</span>
            },
            {
                title: 'Fecha',
                dataIndex: 'capturedAt',
                key: 'capturedAt',
                render: (d, r) => <span className="whitespace-nowrap tabular-nums text-surface-600">{fmtCapture(d, r.source)}</span>
            },
            {
                title: 'Rol',
                dataIndex: 'userType',
                key: 'userType',
                render: (r) => <RoleTag role={r} />
            },
            {
                title: 'Precio (USD)',
                dataIndex: 'priceUsd',
                key: 'priceUsd',
                align: 'right',
                render: (p, r) => (
                    <CapturePrice
                        value={p}
                        isOutlier={r.isOutlier}
                        title="Precio atípico — excluido de los promedios y gráficos"
                    />
                )
            }
        ];
    }

    renderExportModal(exporting, step, elapsed, rowCount, total) {
        return <ExportProgressModal open={exporting} step={step} elapsed={elapsed} rowCount={rowCount} total={total} />;
    }

    renderHeader(withActions) {
        const { exporting } = this.state;
        return (
            <PageHeader
                icon={<DollarSign />}
                title="Levantamiento General"
                description="Todos los levantamientos de precio en un solo lugar — Mary, auditoría y competencia. Precios normalizados a USD."
                actions={
                    withActions ? (
                        <>
                            <Button icon={<ChartColumn />} onClick={() => this.openCharts()}>
                                Ver gráficos
                            </Button>
                            <Button type="primary" icon={<Download />} loading={exporting} onClick={() => this.exportExcel()}>
                                Exportar
                            </Button>
                        </>
                    ) : null
                }
            />
        );
    }

    renderContent() {
        const {
            dataLoading,
            rows,
            clientOptions,
            storeOptions,
            userOptions,
            categoryOptions,
            segmentOptions,
            tagOptions,
            gramsOptions,
            storeTypeOptions,
            dateRange,
            filters,
            pagination
        } = this.state;

        return (
            <>
                <LevantamientoFilters
                    filters={filters}
                    dateRange={dateRange}
                    clientOptions={clientOptions}
                    storeOptions={storeOptions}
                    userOptions={userOptions}
                    categoryOptions={categoryOptions}
                    segmentOptions={segmentOptions}
                    tagOptions={tagOptions}
                    gramsOptions={gramsOptions}
                    storeTypeOptions={storeTypeOptions}
                    showOutlierToggle
                    onFilter={(key, value) => this.setFilter(key, value)}
                    onDateRange={(range) => this.setDateRange(range)}
                    actions={
                        <Button type="text" icon={<Eraser />} onClick={() => this.clearFilters()}>
                            Limpiar
                        </Button>
                    }
                />

                <SectionCard
                    title="Precios capturados"
                    icon={<Rows3 />}
                    flush
                    extra={<span className="text-xs text-surface-500 tabular-nums">{fmtCount(pagination.total)} registros</span>}>
                    <Table
                        columns={this.columns}
                        dataSource={rows}
                        loading={dataLoading}
                        rowKey={(r, i) => `${r.source}-${i}`}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: (
                                <EmptyState
                                    icon={<DollarSign />}
                                    title="Sin precios para los filtros actuales"
                                    description="Amplía el rango de fechas o quita algún filtro."
                                />
                            )
                        }}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ['25', '50', '100', '200'],
                            showTotal: (t) => `${t} registros`,
                            onChange: (page, pageSize) => this.setPage(page, pageSize)
                        }}
                    />
                </SectionCard>
            </>
        );
    }

    render() {
        const { loading, exporting, exportStep, exportElapsed, exportRowCount, accessDenied, error, pagination } = this.state;
        const ready = !accessDenied && !error && !loading;

        return (
            <Layout current="levantamiento-general">
                {this.props.contextHolder}

                {this.renderExportModal(exporting, exportStep, exportElapsed, exportRowCount, pagination.total)}

                {this.renderHeader(ready)}

                {accessDenied ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para administradores y gerentes."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card">
                        <EmptyState tone="danger" icon={<TriangleAlert />} title="Error al cargar el levantamiento general" />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={10} columns={8} />
                ) : (
                    this.renderContent()
                )}
            </Layout>
        );
    }
}

export default withNavigate(LevantamientoGeneral);
